import React, { useEffect, useState } from 'react';
import styles from './VideoSearch.module.scss';
import SectionHeader from '../HomeModern/components/SectionHeader/SectionHeader';
import HighlightCard from '../HomeModern/components/HighlightCard/HighlightCard';
import CollectionCard from '../HomeModern/components/CollectionCard/CollectionCard';
import PromoCard from '../HomeModern/components/PromoCard/PromoCard';
import { fetchHighlights } from '../../../services/highlight.service';
import { fetchCollections } from '../../../services/collection.service';
import { fetchPromos } from '../../../services/promo.service';
import { fetchHomeFightCards } from '../../../services/fight-cards.service';
import { HomeCollection, HomeHighlight, HomePromo } from '../../../types/home.types';
import { HomeFightCard } from '../../../types/fight-cards.types';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { NavigateToInternalUrl } from '../../../utils/api.utils';
import { Routes } from '../../../types/constants';

type FeedStatus = 'loading' | 'success' | 'error';

interface VideoSearchHomeSectionsProps {
    mode: 'idle' | 'searching';
}

const skeletonItems = [0, 1, 2];

const formatEventDate = (isoDate: string) => {
    const parsedDate = new Date(isoDate);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }).format(parsedDate);
};

const renderSkeletonCards = () => skeletonItems.map((item) => (
    <div key={item} className={styles.homeCardSkeleton} aria-hidden="true">
        <div className={styles.homeCardSkeletonMedia} />
        <div className={styles.homeCardSkeletonBody}>
            <div className={styles.homeCardSkeletonLine} />
            <div className={styles.homeCardSkeletonLine} />
            <div className={styles.homeCardSkeletonLineShort} />
        </div>
    </div>
));

/**
 * Renders the Home dynamic card feeds in the Video Search page order.
 */
const VideoSearchHomeSections = ({ mode }: VideoSearchHomeSectionsProps) => {
    const { getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const [highlights, setHighlights] = useState<HomeHighlight[]>([]);
    const [highlightStatus, setHighlightStatus] = useState<FeedStatus>('loading');
    const [collections, setCollections] = useState<HomeCollection[]>([]);
    const [collectionStatus, setCollectionStatus] = useState<FeedStatus>('loading');
    const [fightCards, setFightCards] = useState<HomeFightCard[]>([]);
    const [fightCardStatus, setFightCardStatus] = useState<FeedStatus>('loading');
    const [promos, setPromos] = useState<HomePromo[]>([]);
    const [promoStatus, setPromoStatus] = useState<FeedStatus>('loading');

    useEffect(() => {
        let isMounted = true;
        setHighlightStatus('loading');

        fetchHighlights()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setHighlights(data);
                setHighlightStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Highlight fetch failed', { error });
                setHighlights([]);
                setHighlightStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setCollectionStatus('loading');

        fetchCollections()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setCollections(data);
                setCollectionStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Collection fetch failed', { error });
                setCollections([]);
                setCollectionStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setFightCardStatus('loading');

        fetchHomeFightCards(3, userInfo?.token)
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                const items = (data.items ?? []).map((event) => ({
                    ...event,
                    dateLabel: formatEventDate(event.eventDate)
                }));
                setFightCards(items);
                setFightCardStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Fight cards fetch failed', { error });
                setFightCards([]);
                setFightCardStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, [userInfo?.token]);

    useEffect(() => {
        let isMounted = true;
        setPromoStatus('loading');

        fetchPromos()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setPromos(data);
                setPromoStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Promo fetch failed', { error });
                setPromos([]);
                setPromoStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const shouldRenderHighlights = highlightStatus === 'loading' || highlights.length > 0;
    const shouldRenderCollections = collectionStatus === 'loading' || collections.length > 0;
    const shouldRenderFightCards = fightCardStatus === 'loading' || fightCards.length > 0;
    const shouldRenderPromos = promoStatus === 'loading' || promos.length > 0;

    const handleFightCardBrowse = (event: React.MouseEvent<HTMLAnchorElement>, eventId: string) => {
        event.preventDefault();
        NavigateToInternalUrl(`${Routes.FIGHT_CARD_EVENT}/${eventId}` as Routes);
    };

    const highlightsSection = shouldRenderHighlights ? (
        <section key="highlights" className={styles.homeDiscoverySection}>
            <SectionHeader
                title="Highlights to Watch"
                subtitle={mode === 'idle' ? 'Start with recent fights and fresh moments.' : 'Keep these ready when you finish this search.'}
            />
            <div className={styles.homeDiscoveryGrid} role="list">
                {highlightStatus === 'loading'
                    ? renderSkeletonCards()
                    : highlights.map((highlight) => <HighlightCard key={highlight.id} highlight={highlight} />)}
            </div>
        </section>
    ) : null;

    const collectionsSection = shouldRenderCollections ? (
        <section key="collections" className={styles.homeDiscoverySection}>
            <SectionHeader
                title="Explore Collections"
                subtitle={mode === 'idle' ? 'Tap into curated sets without needing the perfect search term.' : 'Refine your next watch through curated fight groups.'}
            />
            <div className={styles.homeDiscoveryGrid}>
                {collectionStatus === 'loading'
                    ? renderSkeletonCards()
                    : collections.map((collection) => <CollectionCard key={collection.id} collection={collection} />)}
            </div>
        </section>
    ) : null;

    const fightCardsSection = shouldRenderFightCards ? (
        <section key="fight-cards" className={styles.homeDiscoverySection}>
            <SectionHeader
                title="Fight Cards"
                subtitle="Browse by event, lineup, and fight night context."
            />
            <div className={styles.homeDiscoveryGrid}>
                {fightCardStatus === 'loading'
                    ? renderSkeletonCards()
                    : fightCards.map((event) => (
                        <a
                            key={event.id}
                            className={styles.homeFightCard}
                            href={`${Routes.FIGHT_CARD_EVENT}/${event.id}`}
                            onClick={(clickEvent) => handleFightCardBrowse(clickEvent, event.id)}
                            aria-label={`View fight card for ${event.eventName}`}
                        >
                            <div className={styles.homeFightCardMedia}>
                                {event.thumbnailUrl ? (
                                    <img src={event.thumbnailUrl} alt={event.eventName} />
                                ) : (
                                    <div className={styles.homeFightCardMediaPlaceholder} />
                                )}
                            </div>
                            <div className={styles.homeFightCardHeader}>
                                <div>
                                    <h3>{event.eventName}</h3>
                                    <p>{event.location}</p>
                                </div>
                                <span className={styles.homeFightCardDate}>{event.dateLabel}</span>
                            </div>
                            <ul className={styles.homeFightList}>
                                {event.fights.map((fight, index) => (
                                    <li key={`${fight}-${index}`}>{fight}</li>
                                ))}
                            </ul>
                            <span className={styles.homeFightCardCta}>View card</span>
                        </a>
                    ))}
            </div>
        </section>
    ) : null;

    const promosSection = shouldRenderPromos ? (
        <section key="promos" className={styles.homeDiscoverySection}>
            <SectionHeader
                title="Featured Promos"
                subtitle="Event specials and featured offers from Combat TV."
            />
            <div className={styles.homeDiscoveryGrid}>
                {promoStatus === 'loading'
                    ? renderSkeletonCards()
                    : promos.map((promo) => <PromoCard key={promo.id} promo={promo} />)}
            </div>
        </section>
    ) : null;

    const sections = mode === 'idle'
        ? [highlightsSection, collectionsSection, fightCardsSection, promosSection]
        : [collectionsSection, fightCardsSection, highlightsSection, promosSection];
    const visibleSections = sections.filter(Boolean);

    if (visibleSections.length === 0) {
        return null;
    }

    return (
        <div className={[
            styles.homeDiscovery,
            mode === 'searching' ? styles.homeDiscoveryCompact : ''
        ].filter(Boolean).join(' ')}>
            {visibleSections}
        </div>
    );
};

export default VideoSearchHomeSections;
